import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Spinner } from '@fluentui/react-components';
import { ArrowTurnDownRight20Regular, Money16Regular } from '@fluentui/react-icons';

import * as ComponentFunctions from './SettlementConceptsBreakdown.Functions';
import { GetSettlementConceptsBreakdown } from '../../../services/Settlements.Services';
import { SettlementConceptBreakdown } from '../../../models/Entities/Settlements/SettlementConceptBreakdown';
import { formatCurrencyAmountWithIssue } from '../../../commons/currency/Currency.Functions';
import { conceptLabel, groupByCurrency, hasFeeSplit, shareOf } from './SettlementConceptsBreakdown.Logic';

import './SettlementConceptsBreakdown.Styles.css';

/**
 * Totales cobrados por concepto, al pie del detalle de la liquidación. Lo que
 * un concepto incluye de otros se le resta y se muestra como fila propia
 * debajo, así la suma de la columna "Cobrado" es lo que realmente se cobró.
 */
const SettlementConceptsBreakdownComponent = (props: ComponentFunctions.Props) => {
    const [rows, setRows] = useState<SettlementConceptBreakdown[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();

    useEffect(() => {
        if (!props.accessToken || !props.SettlementId || props.Settlement?.isRecalculating) return;
        let cancelled = false;
        setIsLoading(true);
        setError(undefined);
        GetSettlementConceptsBreakdown(props.SettlementId, props.accessToken)
            .then((result) => { if (!cancelled) setRows(result); })
            .catch((failure: any) => { if (!cancelled) setError(failure?.ErrorMessage || failure?.message || 'No se pudo leer el desglose por concepto.'); })
            .finally(() => { if (!cancelled) setIsLoading(false); });
        return () => { cancelled = true; };
        // refreshKey resume estado, recálculo, detalles y totales: cualquiera cambia lo cobrado.
    }, [props.SettlementId, props.accessToken, props.refreshKey, props.Settlement?.isRecalculating]);

    const groups = groupByCurrency(rows);
    const fmt = (amount: number, currency: string) => formatCurrencyAmountWithIssue(amount, currency);
    // Con retenciones o división de honorarios, el bruto no es lo que entra: se abre el detalle y
    // la columna destacada pasa a ser "A cobrar". Si no hay nada de eso, el bruto es lo cobrado.
    const split = hasFeeSplit(rows ?? []);

    return (
        <section className="scb-root" aria-label="Totales cobrados por concepto">
            <header className="scb-header">
                <div className="scb-header__text">
                    <h3 className="scb-title">Totales cobrados por concepto</h3>
                    <p className="scb-intro">
                        Lo que un concepto incluye de otros se le resta y se informa en su propia fila: visita 1000 con viáticos 100 incluidos se muestra como visita 900 y viáticos 100.
                        {split && ' Las retenciones y la división de honorarios se separan de la misma forma; "A cobrar" es el honorario del centro menos su retención.'}
                    </p>
                </div>
                {isLoading && <Spinner size="extra-tiny" />}
            </header>

            {error && <p className="scb-error">{error}</p>}

            {!isLoading && !error && rows !== null && groups.length === 0 && (
                <p className="scb-empty">Todavía no hay importes cobrados por concepto.</p>
            )}

            {groups.map((group) => (
                <div className="scb-group" key={group.currency}>
                    {groups.length > 1 && <span className="scb-currency">{group.currency}</span>}
                    <div className="scb-table-wrap">
                        <table className="scb-table">
                            <thead>
                                <tr>
                                    <th>Concepto</th>
                                    <th className="scb-num">Cant.</th>
                                    <th className="scb-num">Liquidado</th>
                                    <th className="scb-num">Incluye</th>
                                    <th className={`scb-num${split ? '' : ' scb-th-collect'}`}>{split ? 'Bruto' : 'Cobrado'}</th>
                                    {split && <th className="scb-num">Hon. centro</th>}
                                    {split && <th className="scb-num">Hon. equipo</th>}
                                    {split && <th className="scb-num scb-th-ret">Retención</th>}
                                    {split && <th className="scb-num scb-th-collect">A cobrar</th>}
                                    <th className="scb-num">Partic.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {group.items.map((item, index) => {
                                    const included = !!item.billedInConceptId;
                                    return (
                                        <tr key={`${item.conceptId ?? 'none'}-${index}`} className={included ? 'scb-row scb-row--included' : 'scb-row'}>
                                            <td>
                                                <div className="scb-concept">
                                                    {included && <ArrowTurnDownRight20Regular className="scb-concept__arrow" aria-hidden />}
                                                    <span className="scb-concept__name" title={conceptLabel(item)}>{conceptLabel(item)}</span>
                                                    {included && (
                                                        <span className={`scb-tag${item.isBilledConceptCost ? ' scb-tag--cost' : ''}`} title={item.isBilledConceptCost ? 'Cuenta como costo del concepto que lo incluye' : 'Se liquida dentro de otro concepto'}>
                                                            {item.isBilledConceptCost ? 'Costo' : 'Incluido'} en {(item.billedInConceptName || '').trim() || `#${item.billedInConceptId}`}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="scb-num">{item.quantity}</td>
                                            <td className="scb-num scb-muted">{fmt(item.settledAmount, group.currency)}</td>
                                            <td className="scb-num scb-deduct">{item.includedConceptsAmount > 0 ? `− ${fmt(item.includedConceptsAmount, group.currency)}` : <span className="scb-muted">—</span>}</td>
                                            <td className={`scb-num${split ? ' scb-gross' : ' scb-collect'}`}>{fmt(item.amount, group.currency)}</td>
                                            {split && <td className="scb-num">{fmt(item.centerFee, group.currency)}</td>}
                                            {split && <td className="scb-num scb-muted">{item.teamFee ? fmt(item.teamFee, group.currency) : '—'}</td>}
                                            {split && (
                                                <td className="scb-num scb-ret" title={`Centro ${fmt(item.centerWithholding, group.currency)} · Equipo ${fmt(item.teamWithholding, group.currency)}`}>
                                                    {item.withholdingAmount ? `− ${fmt(item.withholdingAmount, group.currency)}` : '—'}
                                                </td>
                                            )}
                                            {split && <td className="scb-num scb-collect">{fmt(item.amountToCollect, group.currency)}</td>}
                                            <td className="scb-num">
                                                <span className="scb-share">
                                                    <span className="scb-share__bar" aria-hidden><span style={{ width: `${shareOf(item, group)}%` }} /></span>
                                                    <span className="scb-share__pct">{shareOf(item, group).toLocaleString('es-AR', { maximumFractionDigits: 1 })}%</span>
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td><span className="scb-total__label"><Money16Regular /> Total cobrado{groups.length > 1 ? ` ${group.currency}` : ''}</span></td>
                                    <td className="scb-num">{group.totals.quantity}</td>
                                    <td className="scb-num scb-muted">{fmt(group.totals.settled, group.currency)}</td>
                                    <td className="scb-num scb-deduct">{group.totals.included > 0 ? `− ${fmt(group.totals.included, group.currency)}` : '—'}</td>
                                    <td className={`scb-num${split ? ' scb-gross' : ' scb-collect scb-total__value'}`}>{fmt(group.totals.amount, group.currency)}</td>
                                    {split && <td className="scb-num">{fmt(group.totals.centerFee, group.currency)}</td>}
                                    {split && <td className="scb-num scb-muted">{fmt(group.totals.teamFee, group.currency)}</td>}
                                    {split && (
                                        <td className="scb-num scb-ret" title={`Centro ${fmt(group.totals.centerWithholding, group.currency)} · Equipo ${fmt(group.totals.teamWithholding, group.currency)}`}>
                                            − {fmt(group.totals.centerWithholding + group.totals.teamWithholding, group.currency)}
                                        </td>
                                    )}
                                    {split && <td className="scb-num scb-collect scb-total__value">{fmt(group.totals.amountToCollect, group.currency)}</td>}
                                    <td className="scb-num">100%</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ))}
        </section>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(SettlementConceptsBreakdownComponent as any);
